// Batch-download AR photos as a single .zip.
//
// AR image URLs come in two flavours, told apart the same way as
// app/hooks/useAuthImageSrc.js does. Presigned S3 URLs (every URL the v0.4
// arimages routes return) carry their own signature and are fetched directly —
// a Bearer header on top makes S3 reject the request. URLs on our API origin
// are auth-protected and go through the `API` client so the JWT interceptor
// attaches the token; some of those endpoints answer with a signed S3 URL
// (text/json) instead of image bytes, in which case we fetch that URL directly.

import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { formatInTimeZone } from 'date-fns-tz';
import API from 'app/api';
import { isProtectedImageUrl } from 'app/hooks/useAuthImageSrc';
import {
  getAugmentedUrl,
  getPhotographer,
  getTimeStampDate,
} from './arImageFields';

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const ZIP_CONCURRENCY = 4;

const sanitize = (value, fallback = 'item') => {
  const cleaned = String(value ?? '')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || fallback;
};

const extFromType = (contentType, url) => {
  const type = String(contentType || '').toLowerCase();
  if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
  if (type.includes('png')) return 'png';
  if (type.includes('webp')) return 'webp';
  if (type.includes('heic')) return 'heic';
  const fromUrl = String(url || '')
    .split('?')[0]
    .split('.')
    .pop();
  return fromUrl && fromUrl.length <= 5 ? fromUrl.toLowerCase() : 'jpg';
};

async function extractUrlFromBlob(blob) {
  const text = (await blob.text()).trim();
  if (!text) return null;
  try {
    const obj = JSON.parse(text);
    const maybeUrl =
      obj?.url ||
      obj?.signedUrl ||
      obj?.location ||
      obj?.data?.url ||
      obj?.data?.signedUrl;
    if (typeof maybeUrl === 'string' && maybeUrl.trim()) {
      return maybeUrl.trim();
    }
  } catch {
    // not JSON — fall through to raw-URL handling
  }
  try {
    return new URL(text).toString();
  } catch {
    return null;
  }
}

async function fetchDirect(url) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Image fetch failed (${res.status})`);
  }
  const blob = await res.blob();
  return { blob, contentType: blob.type || '' };
}

/**
 * Fetch a single (possibly auth-protected) image as a Blob.
 * @returns {Promise<{ blob: Blob, contentType: string }>}
 */
export async function fetchImageBlob(src) {
  if (!src) throw new Error('Missing image URL');

  // Presigned S3 URL (or any other non-API source): no token, fetch as-is.
  if (!isProtectedImageUrl(src)) {
    return fetchDirect(src);
  }

  const response = await API.get(src, { responseType: 'blob' });
  const blob = response?.data;
  if (!blob) throw new Error('Empty image response');

  const contentType =
    response?.headers?.['content-type'] ||
    response?.headers?.['Content-Type'] ||
    blob.type ||
    '';

  if (String(contentType).toLowerCase().startsWith('image/')) {
    return { blob, contentType };
  }

  // Endpoint returned a signed URL rather than bytes — fetch it directly.
  const resolvedUrl = await extractUrlFromBlob(blob);
  if (!resolvedUrl) {
    throw new Error(`Unexpected content-type: ${contentType || '(none)'}`);
  }
  return fetchDirect(resolvedUrl);
}

/** Download one AR photo through the authed pipeline. */
export async function downloadSingleArImage(image) {
  const url = getAugmentedUrl(image);
  const { blob, contentType } = await fetchImageBlob(url);
  saveAs(blob, buildFileName(image, contentType, url));
}

function buildFileName(image, contentType, url, index) {
  const date = getTimeStampDate(image);
  const datePart = date
    ? formatInTimeZone(date, TIME_ZONE, 'yyyy-MM-dd_HHmmss')
    : 'undated';
  const who = sanitize(
    String(getPhotographer(image)).split('@')[0],
    'unknown',
  );
  const id = sanitize(String(image?.id ?? '').slice(0, 8), 'img');
  const suffix = index == null ? '' : `_${String(index + 1).padStart(3, '0')}`;
  return `${datePart}_${who}_${id}${suffix}.${extFromType(contentType, url)}`;
}

/**
 * Bundle the given AR images into one .zip and trigger a download.
 *
 * @param {object[]} images       raw AR image objects
 * @param {string}   projectName  used in the archive filename
 * @param {(done:number,total:number)=>void} [onProgress]
 * @returns {Promise<{ total:number, success:number, failed:object[] }>}
 */
export async function downloadArImagesZip({ images, projectName, onProgress }) {
  const list = (images || []).filter((img) => getAugmentedUrl(img));
  const total = list.length;
  const failed = [];
  let done = 0;

  if (total === 0) {
    return { total: 0, success: 0, failed: [] };
  }

  const zip = new JSZip();
  const usedNames = new Set();

  const addOne = async (image, index) => {
    try {
      const url = getAugmentedUrl(image);
      const { blob, contentType } = await fetchImageBlob(url);
      let name = buildFileName(image, contentType, url, index);
      while (usedNames.has(name)) {
        name = name.replace(/(\.\w+)$/, `_${index}$1`);
      }
      usedNames.add(name);
      zip.file(name, blob);
    } catch (err) {
      failed.push({ id: image?.id, error: err?.message || String(err) });
    } finally {
      done += 1;
      onProgress?.(done, total);
    }
  };

  // Bounded-concurrency worker pool — AR images are large; don't fetch all at once.
  let cursor = 0;
  const worker = async () => {
    while (cursor < list.length) {
      const i = cursor;
      cursor += 1;
      await addOne(list[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(ZIP_CONCURRENCY, list.length) }, worker),
  );

  const success = total - failed.length;
  if (success === 0) {
    return { total, success, failed };
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const stamp = formatInTimeZone(new Date(), TIME_ZONE, 'yyyy-MM-dd');
  saveAs(zipBlob, `ar-photos-${sanitize(projectName, 'project')}-${stamp}.zip`);

  return { total, success, failed };
}
